// filepath: /Users/barryvelasquez/projects/mighty45-web/apps/coach-console/src/components/molecules/DateRangePicker.js
import React from "react";
import {
  TextField,
  Stack,
  Typography,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from "@mui/material";
import moment from "moment";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const limitRange = 365;

// Preset date range options
const PRESETS = {
  CUSTOM: "custom",
  LAST_7_DAYS: "last7days",
  LAST_14_DAYS: "last14days",
  LAST_30_DAYS: "last30days",
  LAST_90_DAYS: "last90days",
  LAST_1_YEAR: "last1year",
  CURRENT_MONTH: "currentMonth",
  PREVIOUS_MONTH: "previousMonth",
};

// Generate dates for each preset
const getPresetDates = (preset) => {
  const today = moment().endOf("day");

  switch (preset) {
    case PRESETS.LAST_7_DAYS:
      return {
        startDate: moment().subtract(6, "days").startOf("day"),
        endDate: today,
        duration: 7,
      };

    case PRESETS.LAST_14_DAYS:
      return {
        startDate: moment().subtract(13, "days").startOf("day"),
        endDate: today,
        duration: 14,
      };

    case PRESETS.LAST_30_DAYS:
      return {
        startDate: moment().subtract(29, "days").startOf("day"),
        endDate: today,
        duration: 30,
      };

    case PRESETS.LAST_90_DAYS:
      return {
        startDate: moment().subtract(89, "days").startOf("day"),
        endDate: today,
        duration: 90,
      };

    case PRESETS.LAST_1_YEAR:
      return {
        startDate: moment().subtract(364, "days").startOf("day"),
        endDate: today,
        duration: 365,
      };

    case PRESETS.CURRENT_MONTH:
      return {
        startDate: moment().startOf("month"),
        endDate: moment().endOf("month"),
        duration: moment().daysInMonth(),
      };

    case PRESETS.PREVIOUS_MONTH: {
      const prevMonth = moment().subtract(1, "month");
      return {
        startDate: prevMonth.startOf("month"),
        endDate: prevMonth.endOf("month"),
        duration: prevMonth.daysInMonth(),
      };
    }

    default:
      return {
        startDate: today.clone().subtract(6, "days").startOf("day"),
        endDate: today,
        duration: 7,
      };
  }
};

/**
 * Validates and ensures dates stay within valid ranges
 * @param {moment.Moment} startDate - The start date
 * @param {moment.Moment} endDate - The end date
 * @returns {Object} - Object containing validated start and end dates
 */
const validateDateRange = (startDate, endDate) => {
  // Ensure we're working with valid moment objects
  const validStartDate =
    moment.isMoment(startDate) && startDate.isValid()
      ? startDate.clone()
      : moment().startOf("day");

  const validEndDate =
    moment.isMoment(endDate) && endDate.isValid()
      ? endDate.clone()
      : validStartDate.clone().add(1, "day");

  // Ensure end date is not before start date
  if (validEndDate.isBefore(validStartDate)) {
    return {
      startDate: validStartDate,
      endDate: validStartDate.clone().add(1, "day"),
      hasWarning: true,
      message: "End date cannot be before start date. Adjusted automatically.",
    };
  }

  // Check if range exceeds limit
  const diff = validEndDate.diff(validStartDate, "days");
  if (diff > limitRange) {
    return {
      startDate: validStartDate,
      endDate: validStartDate.clone().add(limitRange, "days"),
      hasWarning: true,
      message: `Date range cannot exceed ${limitRange} days. Adjusted automatically.`,
    };
  }

  return {
    startDate: validStartDate,
    endDate: validEndDate,
    hasWarning: false,
    message: "",
  };
};

/**
 * Determines which preset (if any) the current date range matches
 * @param {moment.Moment} startDate - Start date
 * @param {moment.Moment} endDate - End date
 * @returns {string} - Preset key or 'custom'
 */
const detectPreset = (startDate, endDate) => {
  // Check for each preset
  for (const preset of Object.values(PRESETS)) {
    if (preset === PRESETS.CUSTOM) continue;

    const presetDates = getPresetDates(preset);
    if (
      startDate.format("YYYY-MM-DD") ===
        presetDates.startDate.format("YYYY-MM-DD") &&
      endDate.format("YYYY-MM-DD") === presetDates.endDate.format("YYYY-MM-DD")
    ) {
      return preset;
    }
  }

  return PRESETS.CUSTOM;
};

/**
 * Lets you choose a date range with validation and limits
 * @param {Object} props - Component props
 * @param {moment.Moment} props.startDate - Initial start date
 * @param {moment.Moment} props.endDate - Initial end date
 * @param {Function} props.onChangeDates - Callback when dates change
 * @returns {React.ReactElement} - Rendered component
 */
function DateRangePicker({
  startDate = moment(),
  endDate = moment(),
  onChangeDates,
}) {
  const [currentStartDate, setCurrentStartDate] = React.useState(startDate);
  const [currentEndDate, setCurrentEndDate] = React.useState(endDate);
  const [warning, setWarning] = React.useState("");
  const [selectedPreset, setSelectedPreset] = React.useState(
    detectPreset(startDate, endDate),
  );

  // Store the duration of the current range
  const [rangeDuration, setRangeDuration] = React.useState(
    currentEndDate.diff(currentStartDate, "days") + 1,
  );

  // Validate initial date range on mount
  React.useEffect(() => {
    const result = validateDateRange(startDate, endDate);
    if (result.hasWarning) {
      setCurrentStartDate(result.startDate);
      setCurrentEndDate(result.endDate);
      setWarning(result.message);
      onChangeDates({ startDate: result.startDate, endDate: result.endDate });
    }
    // We only want to run this once on mount
    // eslint-disable-next-line
  }, []);

  const handleStartDateChange = (event) => {
    try {
      const value = event.target.value;
      // Don't force validation on empty value to allow typing
      if (!value) return;

      const newStartDate = moment(value).startOf("day");
      // Only proceed with valid dates
      if (!newStartDate.isValid()) return;

      // Check if the end date needs adjustment
      let result;
      const diff = currentEndDate.diff(newStartDate, "days");

      if (diff > limitRange) {
        // If too large range, adjust end date
        const newEndDate = newStartDate.clone().add(limitRange, "days");
        result = {
          startDate: newStartDate,
          endDate: newEndDate,
          hasWarning: true,
          message: `Date range cannot exceed ${limitRange} days. End date adjusted.`,
        };
      } else if (currentEndDate.isBefore(newStartDate)) {
        // If end date is before new start date
        const newEndDate = newStartDate.clone().add(1, "days");
        result = {
          startDate: newStartDate,
          endDate: newEndDate,
          hasWarning: true,
          message: "End date cannot be before start date. Adjusted end date.",
        };
      } else {
        // Valid range
        result = {
          startDate: newStartDate,
          endDate: currentEndDate,
          hasWarning: false,
          message: "",
        };
      }

      setCurrentStartDate(result.startDate);
      setCurrentEndDate(result.endDate);
      setWarning(result.message);
      setSelectedPreset(PRESETS.CUSTOM);
      setRangeDuration(result.endDate.diff(result.startDate, "days") + 1);

      onChangeDates({
        startDate: result.startDate,
        endDate: result.endDate,
      });
    } catch (error) {
      console.error("Error handling start date change:", error);
      // Keep existing values in case of error
    }
  };

  const handleEndDateChange = (event) => {
    try {
      const value = event.target.value;
      // Don't force validation on empty value to allow typing
      if (!value) return;

      const newEndDate = moment(value).endOf("day");
      // Only proceed with valid dates
      if (!newEndDate.isValid()) return;

      // Check if the start date needs adjustment
      let result;
      const diff = newEndDate.diff(currentStartDate, "days");

      if (diff > limitRange) {
        // If too large range, adjust start date
        const newStartDate = newEndDate.clone().subtract(limitRange, "days");
        result = {
          startDate: newStartDate,
          endDate: newEndDate,
          hasWarning: true,
          message: `Date range cannot exceed ${limitRange} days. Start date adjusted.`,
        };
      } else if (newEndDate.isBefore(currentStartDate)) {
        // If end date is before start date, adjust end date
        const newEndDate = currentStartDate.clone().add(1, "days");
        result = {
          startDate: currentStartDate,
          endDate: newEndDate,
          hasWarning: true,
          message: "End date cannot be before start date. End date adjusted.",
        };
      } else {
        // Valid range
        result = {
          startDate: currentStartDate,
          endDate: newEndDate,
          hasWarning: false,
          message: "",
        };
      }

      setCurrentStartDate(result.startDate);
      setCurrentEndDate(result.endDate);
      setWarning(result.message);
      setSelectedPreset(PRESETS.CUSTOM);
      setRangeDuration(result.endDate.diff(result.startDate, "days") + 1);

      onChangeDates({
        startDate: result.startDate,
        endDate: result.endDate,
      });
    } catch (error) {
      console.error("Error handling end date change:", error);
      // Keep existing values in case of error
    }
  };

  // Handle preset selection
  const handlePresetChange = (event) => {
    const preset = event.target.value;
    setSelectedPreset(preset);

    // If it's a custom preset, don't change dates
    if (preset === PRESETS.CUSTOM) return;

    const {
      startDate: newStartDate,
      endDate: newEndDate,
      duration,
    } = getPresetDates(preset);

    setCurrentStartDate(newStartDate);
    setCurrentEndDate(newEndDate);
    setRangeDuration(duration);
    setWarning("");

    onChangeDates({
      startDate: newStartDate,
      endDate: newEndDate,
    });
  };

  // Navigate to previous period
  const handlePreviousPeriod = () => {
    const newEndDate = currentStartDate.clone().subtract(1, "day").endOf("day");
    const newStartDate = newEndDate
      .clone()
      .subtract(rangeDuration - 1, "days")
      .startOf("day");

    setCurrentStartDate(newStartDate);
    setCurrentEndDate(newEndDate);
    setSelectedPreset(PRESETS.CUSTOM); // Moving to a custom range
    setWarning("");

    onChangeDates({
      startDate: newStartDate,
      endDate: newEndDate,
    });
  };

  // Navigate to next period
  const handleNextPeriod = () => {
    // Don't allow navigating beyond today
    const today = moment().endOf("day");
    if (currentEndDate.isSameOrAfter(today)) {
      setWarning("Cannot navigate beyond current date");
      setTimeout(() => setWarning(""), 3000);
      return;
    }

    const newStartDate = currentEndDate.clone().add(1, "day").startOf("day");
    let newEndDate = newStartDate
      .clone()
      .add(rangeDuration - 1, "days")
      .endOf("day");

    // If new end date would be beyond today, cap it at today
    if (newEndDate.isAfter(today)) {
      newEndDate = today.clone();
      setWarning("End date adjusted to today's date");
    }

    setCurrentStartDate(newStartDate);
    setCurrentEndDate(newEndDate);
    setSelectedPreset(PRESETS.CUSTOM); // Moving to a custom range

    onChangeDates({
      startDate: newStartDate,
      endDate: newEndDate,
    });
  };

  return (
    <Tooltip title={warning} open={!!warning} arrow placement="top">
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <IconButton
          onClick={handlePreviousPeriod}
          size="small"
          color="primary"
          sx={{ p: 0.5 }}
        >
          <NavigateBeforeIcon />
        </IconButton>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={selectedPreset}
            displayEmpty
            variant="outlined"
            onChange={handlePresetChange}
            sx={{ "& .MuiSelect-select": { py: 0.5 } }}
          >
            <MenuItem value={PRESETS.CUSTOM}>Custom</MenuItem>
            <MenuItem value={PRESETS.LAST_7_DAYS}>Last 7 Days</MenuItem>
            <MenuItem value={PRESETS.LAST_14_DAYS}>Last 14 Days</MenuItem>
            <MenuItem value={PRESETS.LAST_30_DAYS}>Last 30 Days</MenuItem>
            <MenuItem value={PRESETS.LAST_90_DAYS}>Last 90 Days</MenuItem>
            <MenuItem value={PRESETS.LAST_1_YEAR}>Last 1 Year</MenuItem>
            <MenuItem value={PRESETS.CURRENT_MONTH}>Current Month</MenuItem>
            <MenuItem value={PRESETS.PREVIOUS_MONTH}>Previous Month</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          From:
        </Typography>
        <TextField
          size="small"
          type="date"
          value={currentStartDate.format("YYYY-MM-DD")}
          variant="outlined"
          onChange={handleStartDateChange}
          onBlur={() => setTimeout(() => setWarning(""), 3000)}
          InputProps={{
            sx: {
              height: 32,
              "& input": {
                py: 0.5,
                px: 1,
              },
            },
          }}
        />
        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          To:
        </Typography>
        <TextField
          size="small"
          type="date"
          value={currentEndDate.format("YYYY-MM-DD")}
          variant="outlined"
          onChange={handleEndDateChange}
          onBlur={() => setTimeout(() => setWarning(""), 3000)}
          InputProps={{
            sx: {
              height: 32,
              "& input": {
                py: 0.5,
                px: 1,
              },
            },
          }}
        />

        <IconButton
          onClick={handleNextPeriod}
          size="small"
          color="primary"
          disabled={currentEndDate.isSameOrAfter(moment().endOf("day"))}
          sx={{ p: 0.5 }}
        >
          <NavigateNextIcon />
        </IconButton>
      </Stack>
    </Tooltip>
  );
}

export default DateRangePicker;
